import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const LangContext = createContext(null);

// Currency definitions. USD is the main currency.
export const CURRENCIES = {
  USD: { symbol: '$',  rate: 1,      label: 'USD', code: 'USD' },
  EUR: { symbol: '€',  rate: 0.92,   label: 'EUR', code: 'EUR' },
  INR: { symbol: '₹',  rate: 83,     label: 'INR', code: 'INR' },
};

export const LANGUAGES = [
  { code: 'en', label: 'English',  flag: 'EN' },
  { code: 'de', label: 'Deutsch',  flag: 'DE' },
  { code: 'nl', label: 'Nederlands', flag: 'NL' },
  { code: 'es', label: 'Español',  flag: 'ES' },
  { code: 'fr', label: 'Français', flag: 'FR' },
  { code: 'it', label: 'Italiano', flag: 'IT' },
];

// Translation dictionary — covers all key user-facing strings on the marketing
// site and the most visible dashboard chrome. Non-translated UI inherits English.
const T = {
  en: {
    'nav.products': 'Products', 'nav.pricing': 'Pricing', 'nav.case_studies': 'Case Studies',
    'nav.signin': 'Sign In', 'nav.start_trial': 'Start Free Trial',
    'hero.badge': 'Powered by Open Source AI',
    'hero.title_1': 'The Operating System', 'hero.title_2': 'for',
    'hero.title_3': 'Marketing Intelligence',
    'hero.subtitle': 'Unify 28+ channels. Spot revenue blind spots. Deploy AI agents that plan, execute, and optimize your entire media operation.',
    'hero.cta_trial': 'Start 7-Day Free Trial', 'hero.cta_demo': 'Live Dashboard',
    'hero.note': 'No credit card required · Cancel anytime · 7-day free trial',
    'social.trusted': 'Trusted by growth teams managing $12M+ in ad spend',
    'eco.title_1': 'Four products.', 'eco.title_2': 'One ecosystem.',
    'eco.subtitle': 'From insight to action in a single platform. Open source AI at the core.',
    'how.eyebrow': 'HOW IT WORKS',
    'how.title': 'Your data. Your models. Your orchestra.',
    'how.subtitle': 'OmniView is the conductor — never the owner of your data. Connect your platforms, plug in your knowledge base and your choice of LLM, and we orchestrate an agentic AI infrastructure unique to your business.',
    'how.step1.title': 'You & Your Team',
    'how.step1.desc': 'Marketers, analysts and CXOs work side-by-side with OmniView through one unified workspace — no spreadsheets, no tab-switching.',
    'how.step2.title': 'Connect Platforms & Tools',
    'how.step2.desc': 'Plug in 28+ ad platforms, marketplaces, GA4, CRM and Sheets. Connect your existing business knowledge base — playbooks, brand docs, historical data.',
    'how.step3.title': 'Choose Your LLM',
    'how.step3.desc': 'Bring any model: GPT-5.2, Claude, Gemini — or self-host open-source Qwen 3.6, Llama or Mistral. You decide the brain.',
    'how.step4.title': 'Agentic AI, Unique to You',
    'how.step4.desc': 'OmniView orchestrates agents for media planning, deployment and optimisation that are bespoke to your data, brand voice and goals.',
    'how.gdpr.badge': 'GDPR · EU DATA PROTECTION · ENCRYPTED',
    'how.gdpr.title': 'The most privacy-respecting marketing OS on the planet.',
    'how.gdpr.desc': 'OmniView never stores your client data. All data you generate, collect or analyse is encrypted and stored on your own dedicated server — accessible only to you. We are the orchestrator, not the owner. This makes OmniView the most GDPR-aligned and EU-Data-Protection compliant marketing platform available today.',
    'how.gdpr.point1': 'Zero data residency on OmniView servers',
    'how.gdpr.point2': 'End-to-end encrypted user-owned storage',
    'how.gdpr.point3': 'Bring-your-own-LLM (paid or open source)',
    'how.gdpr.point4': 'Full audit log, right to be forgotten, on demand',
    'steps.title': 'Launch in 3 steps',
    'steps.subtitle': 'From signup to live dashboard in under 10 minutes. Your AI agent handles the heavy lifting.',
    'steps.s1.title': 'Sign Up & Connect',
    'steps.s1.desc': 'Start your 7-day trial. Connect your first channel in 2 minutes. Google Ads, Meta, Amazon, Shopify — one click each.',
    'steps.s2.title': 'Your Agent Sets Up',
    'steps.s2.desc': 'An OmniClaw agent configures your dashboard, maps your data, and identifies your first blind spots automatically.',
    'steps.s3.title': 'See, Plan, Deploy',
    'steps.s3.desc': 'View unified analytics. Let OmniPlan generate your media plan. Deploy with OmniClaw. Watch your ROAS climb.',
    'cases.title': 'Results that speak',
    'cases.subtitle': 'Real outcomes. No names, no faces. Just the work and the numbers.',
    'pricing.title': 'Simple, transparent pricing',
    'pricing.subtitle': 'Start free. Scale as you grow. Cancel anytime.',
    'pricing.monthly': 'Monthly', 'pricing.annual': 'Annual', 'pricing.save': 'Save 20%',
    'pricing.popular': 'MOST POPULAR',
    'cta.title': 'Stop stitching reports.', 'cta.title2': 'Start running marketing.',
    'cta.subtitle': '7-day free trial. No credit card. Cancel anytime.',
    'footer.product': 'Product', 'footer.company': 'Company', 'footer.resources': 'Resources',
    'common.language': 'Language', 'common.currency': 'Currency',
    'dash.connect': 'Connect', 'dash.connected': 'Connected', 'dash.connecting': 'Connecting…',
    'dash.connect_success': 'Connected successfully',
  },
  de: {
    'nav.products': 'Produkte', 'nav.pricing': 'Preise', 'nav.case_studies': 'Fallstudien',
    'nav.signin': 'Anmelden', 'nav.start_trial': 'Kostenlos testen',
    'hero.badge': 'Mit Open-Source-KI',
    'hero.title_1': 'Das Betriebssystem', 'hero.title_2': 'für',
    'hero.title_3': 'Marketing-Intelligenz',
    'hero.subtitle': 'Vereinen Sie 28+ Kanäle. Erkennen Sie blinde Flecken. Setzen Sie KI-Agenten ein, die Ihre gesamte Medienoperation planen, ausführen und optimieren.',
    'hero.cta_trial': '7-Tage kostenlos testen', 'hero.cta_demo': 'Live-Dashboard',
    'hero.note': 'Keine Kreditkarte nötig · Jederzeit kündbar · 7 Tage gratis',
    'social.trusted': 'Vertraut von Wachstumsteams mit über 12 Mio. $ Werbeausgaben',
    'eco.title_1': 'Vier Produkte.', 'eco.title_2': 'Ein Ökosystem.',
    'eco.subtitle': 'Von der Erkenntnis zur Aktion in einer Plattform. Open-Source-KI im Kern.',
    'how.eyebrow': 'SO FUNKTIONIERT ES',
    'how.title': 'Ihre Daten. Ihre Modelle. Ihr Orchester.',
    'how.subtitle': 'OmniView ist der Dirigent — nie der Eigentümer Ihrer Daten. Verbinden Sie Ihre Plattformen, Ihre Wissensdatenbank und das LLM Ihrer Wahl, und wir orchestrieren eine agentenbasierte KI-Infrastruktur, die einzigartig für Ihr Unternehmen ist.',
    'how.step1.title': 'Sie & Ihr Team',
    'how.step1.desc': 'Marketer, Analysten und CXOs arbeiten in einem einheitlichen Arbeitsbereich Seite an Seite mit OmniView — ohne Tabellen, ohne Tab-Wechsel.',
    'how.step2.title': 'Plattformen & Tools verbinden',
    'how.step2.desc': 'Schließen Sie 28+ Werbeplattformen, Marktplätze, GA4, CRM und Sheets an. Verbinden Sie Ihre vorhandene Wissensdatenbank — Playbooks, Markendokumente, historische Daten.',
    'how.step3.title': 'Wählen Sie Ihr LLM',
    'how.step3.desc': 'Bringen Sie jedes Modell mit: GPT-5.2, Claude, Gemini — oder hosten Sie Open-Source-Qwen 3.6, Llama oder Mistral selbst. Sie wählen das Gehirn.',
    'how.step4.title': 'Agentenbasierte KI, einzigartig für Sie',
    'how.step4.desc': 'OmniView orchestriert Agenten für Mediaplanung, Bereitstellung und Optimierung, die auf Ihre Daten, Markenstimme und Ziele zugeschnitten sind.',
    'how.gdpr.badge': 'DSGVO · EU-DATENSCHUTZ · VERSCHLÜSSELT',
    'how.gdpr.title': 'Das datenschutzfreundlichste Marketing-OS der Welt.',
    'how.gdpr.desc': 'OmniView speichert niemals Ihre Kundendaten. Alle Daten, die Sie erzeugen, sammeln oder analysieren, werden verschlüsselt auf Ihrem eigenen dedizierten Server gespeichert — nur für Sie zugänglich. Wir sind der Orchestrator, nicht der Eigentümer. Damit ist OmniView die heute am stärksten DSGVO- und EU-Datenschutz-konforme Marketingplattform.',
    'how.gdpr.point1': 'Null Datenresidenz auf OmniView-Servern',
    'how.gdpr.point2': 'Ende-zu-Ende verschlüsselter, nutzereigener Speicher',
    'how.gdpr.point3': 'Eigenes LLM (kostenpflichtig oder Open Source)',
    'how.gdpr.point4': 'Vollständiges Audit-Log, Recht auf Vergessenwerden auf Anfrage',
    'steps.title': 'Start in 3 Schritten',
    'steps.subtitle': 'Vom Login bis zum Live-Dashboard in unter 10 Minuten. Ihr KI-Agent übernimmt die Schwerstarbeit.',
    'steps.s1.title': 'Anmelden & Verbinden',
    'steps.s1.desc': 'Starten Sie Ihre 7-Tage-Testphase. Verbinden Sie Ihren ersten Kanal in 2 Minuten — ein Klick je Plattform.',
    'steps.s2.title': 'Ihr Agent richtet ein',
    'steps.s2.desc': 'Ein OmniClaw-Agent konfiguriert Ihr Dashboard, ordnet Ihre Daten zu und identifiziert automatisch erste blinde Flecken.',
    'steps.s3.title': 'Sehen, Planen, Bereitstellen',
    'steps.s3.desc': 'Vereinheitlichte Analysen ansehen. OmniPlan erstellt Ihren Medienplan. OmniClaw setzt um. Beobachten Sie Ihren ROAS steigen.',
    'cases.title': 'Ergebnisse, die zählen',
    'cases.subtitle': 'Echte Resultate. Keine Namen, keine Gesichter. Nur die Arbeit und die Zahlen.',
    'pricing.title': 'Einfache, transparente Preise',
    'pricing.subtitle': 'Kostenlos starten. Skalieren beim Wachsen. Jederzeit kündbar.',
    'pricing.monthly': 'Monatlich', 'pricing.annual': 'Jährlich', 'pricing.save': '20% sparen',
    'pricing.popular': 'AM BELIEBTESTEN',
    'cta.title': 'Schluss mit Reports stitchen.', 'cta.title2': 'Starten Sie Marketing.',
    'cta.subtitle': '7 Tage kostenlos. Keine Kreditkarte. Jederzeit kündbar.',
    'footer.product': 'Produkt', 'footer.company': 'Unternehmen', 'footer.resources': 'Ressourcen',
    'common.language': 'Sprache', 'common.currency': 'Währung',
    'dash.connect': 'Verbinden', 'dash.connected': 'Verbunden', 'dash.connecting': 'Verbinde…',
    'dash.connect_success': 'Erfolgreich verbunden',
  },
  nl: {
    'nav.products': 'Producten', 'nav.pricing': 'Prijzen', 'nav.case_studies': 'Cases',
    'nav.signin': 'Inloggen', 'nav.start_trial': 'Gratis proefperiode',
    'hero.badge': 'Aangedreven door open-source AI',
    'hero.title_1': 'Het besturingssysteem', 'hero.title_2': 'voor',
    'hero.title_3': 'marketing-intelligentie',
    'hero.subtitle': 'Verenig 28+ kanalen. Ontdek verborgen omzetlekken. Zet AI-agents in die uw hele media-operatie plannen, uitvoeren en optimaliseren.',
    'hero.cta_trial': 'Start 7 dagen gratis', 'hero.cta_demo': 'Live dashboard',
    'hero.note': 'Geen creditcard nodig · Altijd opzegbaar · 7 dagen gratis',
    'social.trusted': 'Vertrouwd door groeiteams die meer dan $12M aan advertenties beheren',
    'eco.title_1': 'Vier producten.', 'eco.title_2': 'Eén ecosysteem.',
    'eco.subtitle': 'Van inzicht naar actie in één platform. Open-source AI als kern.',
    'how.eyebrow': 'HOE HET WERKT',
    'how.title': 'Uw data. Uw modellen. Uw orkest.',
    'how.subtitle': 'OmniView is de dirigent — nooit de eigenaar van uw data. Koppel uw platforms, kennisbank en het LLM van uw keuze, en wij orkestreren een agentische AI-infrastructuur die uniek is voor uw bedrijf.',
    'how.step1.title': 'U en uw team',
    'how.step1.desc': 'Marketeers, analisten en CXO\'s werken samen met OmniView in één werkruimte — geen sheets, geen tab-switching.',
    'how.step2.title': 'Verbind platforms en tools',
    'how.step2.desc': 'Koppel 28+ advertentieplatforms, marktplaatsen, GA4, CRM en Sheets. Sluit uw bestaande kennisbank aan — playbooks, merkdocumenten, historische data.',
    'how.step3.title': 'Kies uw LLM',
    'how.step3.desc': 'Breng elk model mee: GPT-5.2, Claude, Gemini — of host zelf open-source Qwen 3.6, Llama of Mistral. U kiest het brein.',
    'how.step4.title': 'Agentische AI, uniek voor u',
    'how.step4.desc': 'OmniView orkestreert agents voor mediaplanning, deployment en optimalisatie, op maat van uw data, merkstem en doelen.',
    'how.gdpr.badge': 'AVG · EU-GEGEVENSBESCHERMING · VERSLEUTELD',
    'how.gdpr.title': 'Het meest privacy-vriendelijke marketing-OS ter wereld.',
    'how.gdpr.desc': 'OmniView slaat uw klantgegevens nooit op. Alle data die u genereert, verzamelt of analyseert wordt versleuteld op uw eigen toegewijde server bewaard — alleen voor u toegankelijk. Wij zijn de orkestrator, niet de eigenaar. Dat maakt OmniView het meest AVG- en EU-conform marketingplatform op de markt.',
    'how.gdpr.point1': 'Geen data-residentie op OmniView-servers',
    'how.gdpr.point2': 'End-to-end versleutelde, gebruikerseigen opslag',
    'how.gdpr.point3': 'Eigen LLM meenemen (betaald of open source)',
    'how.gdpr.point4': 'Volledige audit-log, recht om vergeten te worden',
    'steps.title': 'Live in 3 stappen',
    'steps.subtitle': 'Van aanmelden tot live dashboard in minder dan 10 minuten. Uw AI-agent doet het zware werk.',
    'steps.s1.title': 'Aanmelden & koppelen',
    'steps.s1.desc': 'Start uw 7-daagse proefperiode. Koppel uw eerste kanaal in 2 minuten — één klik per platform.',
    'steps.s2.title': 'Uw agent richt in',
    'steps.s2.desc': 'Een OmniClaw-agent configureert uw dashboard, mapt uw data en spot direct de eerste blinde vlekken.',
    'steps.s3.title': 'Bekijken, plannen, uitvoeren',
    'steps.s3.desc': 'Bekijk unified analytics. OmniPlan maakt uw mediaplan. OmniClaw zet het in. Zie uw ROAS stijgen.',
    'cases.title': 'Resultaten die spreken',
    'cases.subtitle': 'Echte uitkomsten. Geen namen, geen gezichten. Alleen het werk en de cijfers.',
    'pricing.title': 'Eenvoudige, transparante prijzen',
    'pricing.subtitle': 'Begin gratis. Schaal mee. Altijd opzegbaar.',
    'pricing.monthly': 'Maandelijks', 'pricing.annual': 'Jaarlijks', 'pricing.save': '20% korting',
    'pricing.popular': 'MEEST GEKOZEN',
    'cta.title': 'Stop met rapporten stitchen.', 'cta.title2': 'Begin met marketing draaien.',
    'cta.subtitle': '7 dagen gratis. Geen creditcard. Altijd opzegbaar.',
    'footer.product': 'Product', 'footer.company': 'Bedrijf', 'footer.resources': 'Resources',
    'common.language': 'Taal', 'common.currency': 'Valuta',
    'dash.connect': 'Verbinden', 'dash.connected': 'Verbonden', 'dash.connecting': 'Verbinden…',
    'dash.connect_success': 'Succesvol verbonden',
  },
  es: {
    'nav.products': 'Productos', 'nav.pricing': 'Precios', 'nav.case_studies': 'Casos',
    'nav.signin': 'Iniciar sesión', 'nav.start_trial': 'Prueba gratuita',
    'hero.badge': 'Impulsado por IA open source',
    'hero.title_1': 'El sistema operativo', 'hero.title_2': 'para',
    'hero.title_3': 'inteligencia de marketing',
    'hero.subtitle': 'Unifica 28+ canales. Descubre fugas de ingresos. Despliega agentes IA que planifican, ejecutan y optimizan toda tu operación de medios.',
    'hero.cta_trial': 'Empezar 7 días gratis', 'hero.cta_demo': 'Dashboard en vivo',
    'hero.note': 'Sin tarjeta · Cancela cuando quieras · 7 días gratis',
    'social.trusted': 'Confiado por equipos de crecimiento con +12M$ en publicidad',
    'eco.title_1': 'Cuatro productos.', 'eco.title_2': 'Un ecosistema.',
    'eco.subtitle': 'De la idea a la acción en una sola plataforma. IA open source en el núcleo.',
    'how.eyebrow': 'CÓMO FUNCIONA',
    'how.title': 'Tus datos. Tus modelos. Tu orquesta.',
    'how.subtitle': 'OmniView es el director — nunca el dueño de tus datos. Conecta tus plataformas, tu base de conocimiento y el LLM que prefieras, y orquestamos una infraestructura de IA agéntica única para tu negocio.',
    'how.step1.title': 'Tú y tu equipo',
    'how.step1.desc': 'Marketers, analistas y CXOs trabajan junto a OmniView en un espacio unificado — sin hojas de cálculo, sin saltar de pestañas.',
    'how.step2.title': 'Conecta plataformas y herramientas',
    'how.step2.desc': 'Conecta 28+ plataformas, marketplaces, GA4, CRM y Sheets. Suma tu base de conocimiento existente — playbooks, documentos de marca, datos históricos.',
    'how.step3.title': 'Elige tu LLM',
    'how.step3.desc': 'Trae cualquier modelo: GPT-5.2, Claude, Gemini — o aloja open-source Qwen 3.6, Llama o Mistral. Tú eliges el cerebro.',
    'how.step4.title': 'IA agéntica, única para ti',
    'how.step4.desc': 'OmniView orquesta agentes de planificación, despliegue y optimización de medios adaptados a tus datos, voz de marca y objetivos.',
    'how.gdpr.badge': 'RGPD · PROTECCIÓN DE DATOS UE · CIFRADO',
    'how.gdpr.title': 'El SO de marketing más respetuoso con la privacidad del planeta.',
    'how.gdpr.desc': 'OmniView nunca almacena tus datos de cliente. Todos los datos que generas, recoges o analizas se cifran y guardan en tu propio servidor dedicado — accesible solo para ti. Somos el orquestador, no el propietario. Esto convierte a OmniView en la plataforma de marketing más alineada con el RGPD y las leyes de protección de datos de la UE.',
    'how.gdpr.point1': 'Cero residencia de datos en servidores OmniView',
    'how.gdpr.point2': 'Almacenamiento cifrado de extremo a extremo, propiedad del usuario',
    'how.gdpr.point3': 'Trae tu propio LLM (de pago u open source)',
    'how.gdpr.point4': 'Registro de auditoría completo y derecho al olvido bajo demanda',
    'steps.title': 'Lanza en 3 pasos',
    'steps.subtitle': 'Del registro al dashboard en vivo en menos de 10 minutos. Tu agente IA hace el trabajo pesado.',
    'steps.s1.title': 'Regístrate y conecta',
    'steps.s1.desc': 'Empieza tu prueba de 7 días. Conecta tu primer canal en 2 minutos — un clic por plataforma.',
    'steps.s2.title': 'Tu agente se configura',
    'steps.s2.desc': 'Un agente OmniClaw configura tu dashboard, mapea tus datos e identifica las primeras fugas automáticamente.',
    'steps.s3.title': 'Ver, planear, desplegar',
    'steps.s3.desc': 'Visualiza analíticas unificadas. OmniPlan genera tu plan de medios. OmniClaw lo despliega. Mira subir tu ROAS.',
    'cases.title': 'Resultados que hablan',
    'cases.subtitle': 'Resultados reales. Sin nombres, sin caras. Solo el trabajo y los números.',
    'pricing.title': 'Precios simples y transparentes',
    'pricing.subtitle': 'Empieza gratis. Escala al crecer. Cancela cuando quieras.',
    'pricing.monthly': 'Mensual', 'pricing.annual': 'Anual', 'pricing.save': 'Ahorra 20%',
    'pricing.popular': 'MÁS POPULAR',
    'cta.title': 'Deja de unir reportes.', 'cta.title2': 'Empieza a hacer marketing.',
    'cta.subtitle': '7 días gratis. Sin tarjeta. Cancela cuando quieras.',
    'footer.product': 'Producto', 'footer.company': 'Empresa', 'footer.resources': 'Recursos',
    'common.language': 'Idioma', 'common.currency': 'Moneda',
    'dash.connect': 'Conectar', 'dash.connected': 'Conectado', 'dash.connecting': 'Conectando…',
    'dash.connect_success': 'Conectado con éxito',
  },
  fr: {
    'nav.products': 'Produits', 'nav.pricing': 'Tarifs', 'nav.case_studies': 'Études de cas',
    'nav.signin': 'Connexion', 'nav.start_trial': 'Essai gratuit',
    'hero.badge': 'Propulsé par l\'IA open source',
    'hero.title_1': 'Le système d\'exploitation', 'hero.title_2': 'pour',
    'hero.title_3': 'l\'intelligence marketing',
    'hero.subtitle': 'Unifiez 28+ canaux. Détectez les angles morts de revenus. Déployez des agents IA qui planifient, exécutent et optimisent toute votre opération média.',
    'hero.cta_trial': 'Essayez 7 jours gratuits', 'hero.cta_demo': 'Tableau de bord en direct',
    'hero.note': 'Sans carte bancaire · Annulez à tout moment · 7 jours gratuits',
    'social.trusted': 'Choisi par des équipes growth gérant plus de 12 M$ d\'investissement publicitaire',
    'eco.title_1': 'Quatre produits.', 'eco.title_2': 'Un écosystème.',
    'eco.subtitle': 'De l\'insight à l\'action sur une seule plateforme. IA open source au cœur.',
    'how.eyebrow': 'COMMENT ÇA MARCHE',
    'how.title': 'Vos données. Vos modèles. Votre orchestre.',
    'how.subtitle': 'OmniView est le chef d\'orchestre — jamais le propriétaire de vos données. Connectez vos plateformes, votre base de connaissances et le LLM de votre choix, et nous orchestrons une infrastructure d\'IA agentique unique pour votre entreprise.',
    'how.step1.title': 'Vous et votre équipe',
    'how.step1.desc': 'Marketeurs, analystes et CXO travaillent avec OmniView dans un espace unifié — fini les tableurs et les changements d\'onglet.',
    'how.step2.title': 'Connectez plateformes et outils',
    'how.step2.desc': 'Branchez 28+ plateformes pub, marketplaces, GA4, CRM et Sheets. Ajoutez votre base de connaissances — playbooks, documents de marque, historique.',
    'how.step3.title': 'Choisissez votre LLM',
    'how.step3.desc': 'Apportez n\'importe quel modèle : GPT-5.2, Claude, Gemini — ou hébergez vous-même Qwen 3.6, Llama ou Mistral. Vous choisissez le cerveau.',
    'how.step4.title': 'IA agentique, unique pour vous',
    'how.step4.desc': 'OmniView orchestre des agents de planification, déploiement et optimisation média sur mesure pour vos données, votre voix de marque et vos objectifs.',
    'how.gdpr.badge': 'RGPD · PROTECTION DES DONNÉES UE · CHIFFRÉ',
    'how.gdpr.title': 'L\'OS marketing le plus respectueux de la vie privée au monde.',
    'how.gdpr.desc': 'OmniView ne stocke jamais vos données client. Toutes les données que vous générez, collectez ou analysez sont chiffrées et stockées sur votre propre serveur dédié — accessibles uniquement par vous. Nous sommes l\'orchestrateur, pas le propriétaire. Cela fait d\'OmniView la plateforme marketing la plus conforme RGPD et lois UE de protection des données disponible aujourd\'hui.',
    'how.gdpr.point1': 'Zéro résidence de données sur les serveurs OmniView',
    'how.gdpr.point2': 'Stockage chiffré de bout en bout, propriété de l\'utilisateur',
    'how.gdpr.point3': 'BYO-LLM (payant ou open source)',
    'how.gdpr.point4': 'Audit complet, droit à l\'oubli sur demande',
    'steps.title': 'Lancez en 3 étapes',
    'steps.subtitle': 'De l\'inscription au tableau de bord en moins de 10 minutes. Votre agent IA gère le gros du travail.',
    'steps.s1.title': 'Inscrivez-vous et connectez',
    'steps.s1.desc': 'Commencez votre essai 7 jours. Connectez votre premier canal en 2 minutes — un clic par plateforme.',
    'steps.s2.title': 'Votre agent configure',
    'steps.s2.desc': 'Un agent OmniClaw configure votre tableau de bord, mappe vos données et identifie vos premiers angles morts automatiquement.',
    'steps.s3.title': 'Voir, planifier, déployer',
    'steps.s3.desc': 'Visualisez l\'analytique unifiée. OmniPlan génère votre plan média. OmniClaw le déploie. Regardez votre ROAS grimper.',
    'cases.title': 'Des résultats qui parlent',
    'cases.subtitle': 'Vrais résultats. Sans noms, sans visages. Juste le travail et les chiffres.',
    'pricing.title': 'Tarifs simples et transparents',
    'pricing.subtitle': 'Commencez gratuitement. Évoluez avec votre croissance. Annulez à tout moment.',
    'pricing.monthly': 'Mensuel', 'pricing.annual': 'Annuel', 'pricing.save': '-20%',
    'pricing.popular': 'LE PLUS POPULAIRE',
    'cta.title': 'Arrêtez d\'assembler des rapports.', 'cta.title2': 'Démarrez votre marketing.',
    'cta.subtitle': '7 jours gratuits. Sans carte bancaire. Annulez à tout moment.',
    'footer.product': 'Produit', 'footer.company': 'Entreprise', 'footer.resources': 'Ressources',
    'common.language': 'Langue', 'common.currency': 'Devise',
    'dash.connect': 'Connecter', 'dash.connected': 'Connecté', 'dash.connecting': 'Connexion…',
    'dash.connect_success': 'Connecté avec succès',
  },
  it: {
    'nav.products': 'Prodotti', 'nav.pricing': 'Prezzi', 'nav.case_studies': 'Casi studio',
    'nav.signin': 'Accedi', 'nav.start_trial': 'Prova gratuita',
    'hero.badge': 'Basato su IA open source',
    'hero.title_1': 'Il sistema operativo', 'hero.title_2': 'per',
    'hero.title_3': 'l\'intelligenza di marketing',
    'hero.subtitle': 'Unifica 28+ canali. Trova le perdite di fatturato. Distribuisci agenti IA che pianificano, eseguono e ottimizzano l\'intera operazione media.',
    'hero.cta_trial': 'Inizia 7 giorni gratis', 'hero.cta_demo': 'Dashboard live',
    'hero.note': 'Nessuna carta · Annulla quando vuoi · 7 giorni gratis',
    'social.trusted': 'Scelta da team growth che gestiscono oltre 12M$ di spesa pubblicitaria',
    'eco.title_1': 'Quattro prodotti.', 'eco.title_2': 'Un ecosistema.',
    'eco.subtitle': 'Dall\'insight all\'azione in un\'unica piattaforma. IA open source al cuore.',
    'how.eyebrow': 'COME FUNZIONA',
    'how.title': 'I tuoi dati. I tuoi modelli. La tua orchestra.',
    'how.subtitle': 'OmniView è il direttore — mai il proprietario dei tuoi dati. Collega le piattaforme, la knowledge base e l\'LLM che preferisci, e orchestriamo un\'infrastruttura di IA agentica unica per la tua azienda.',
    'how.step1.title': 'Tu e il tuo team',
    'how.step1.desc': 'Marketer, analisti e CXO lavorano con OmniView in uno spazio unificato — niente fogli, niente cambio di tab.',
    'how.step2.title': 'Collega piattaforme e strumenti',
    'how.step2.desc': 'Collega 28+ piattaforme ads, marketplace, GA4, CRM e Sheets. Aggiungi la knowledge base esistente — playbook, documenti di brand, dati storici.',
    'how.step3.title': 'Scegli il tuo LLM',
    'how.step3.desc': 'Porta qualunque modello: GPT-5.2, Claude, Gemini — o self-hosta Qwen 3.6, Llama o Mistral open source. Tu scegli il cervello.',
    'how.step4.title': 'IA agentica, unica per te',
    'how.step4.desc': 'OmniView orchestra agenti per pianificazione, deploy e ottimizzazione media su misura per dati, tono di brand e obiettivi.',
    'how.gdpr.badge': 'GDPR · PROTEZIONE DATI UE · CRITTOGRAFATO',
    'how.gdpr.title': 'L\'OS di marketing più rispettoso della privacy al mondo.',
    'how.gdpr.desc': 'OmniView non memorizza mai i tuoi dati client. Tutti i dati che generi, raccogli o analizzi sono crittografati e archiviati sul tuo server dedicato — accessibili solo a te. Siamo l\'orchestratore, non il proprietario. Questo rende OmniView la piattaforma di marketing più conforme a GDPR e leggi UE sulla protezione dei dati disponibile oggi.',
    'how.gdpr.point1': 'Zero data residency sui server OmniView',
    'how.gdpr.point2': 'Storage crittografato end-to-end di proprietà dell\'utente',
    'how.gdpr.point3': 'Bring-your-own-LLM (a pagamento o open source)',
    'how.gdpr.point4': 'Audit log completo, diritto all\'oblio su richiesta',
    'steps.title': 'In 3 passi sei live',
    'steps.subtitle': 'Dalla registrazione alla dashboard live in meno di 10 minuti. Il tuo agente IA fa il lavoro pesante.',
    'steps.s1.title': 'Registrati e collega',
    'steps.s1.desc': 'Inizia la tua prova di 7 giorni. Collega il primo canale in 2 minuti — un click per piattaforma.',
    'steps.s2.title': 'Il tuo agente configura',
    'steps.s2.desc': 'Un agente OmniClaw configura la dashboard, mappa i dati e individua subito i primi blind spot.',
    'steps.s3.title': 'Vedi, pianifica, distribuisci',
    'steps.s3.desc': 'Visualizza analytics unificate. OmniPlan genera il piano media. OmniClaw lo distribuisce. Guarda crescere il ROAS.',
    'cases.title': 'Risultati che parlano',
    'cases.subtitle': 'Risultati reali. Niente nomi, niente volti. Solo il lavoro e i numeri.',
    'pricing.title': 'Prezzi semplici e trasparenti',
    'pricing.subtitle': 'Inizia gratis. Scala con la crescita. Annulla quando vuoi.',
    'pricing.monthly': 'Mensile', 'pricing.annual': 'Annuale', 'pricing.save': 'Risparmia 20%',
    'pricing.popular': 'PIÙ POPOLARE',
    'cta.title': 'Smetti di cucire report.', 'cta.title2': 'Inizia a fare marketing.',
    'cta.subtitle': '7 giorni gratis. Nessuna carta. Annulla quando vuoi.',
    'footer.product': 'Prodotto', 'footer.company': 'Azienda', 'footer.resources': 'Risorse',
    'common.language': 'Lingua', 'common.currency': 'Valuta',
    'dash.connect': 'Collega', 'dash.connected': 'Collegato', 'dash.connecting': 'Collegamento…',
    'dash.connect_success': 'Collegato con successo',
  },
};

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem('omniview_lang') || 'en'; } catch { return 'en'; }
  });
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem('omniview_currency') || 'USD'; } catch { return 'USD'; }
  });

  const setLang = useCallback((code) => {
    setLangState(code);
    try { localStorage.setItem('omniview_lang', code); } catch { /* noop */ }
    try { document.documentElement.lang = code; } catch { /* noop */ }
  }, []);

  const setCurrency = useCallback((code) => {
    setCurrencyState(code);
    try { localStorage.setItem('omniview_currency', code); } catch { /* noop */ }
  }, []);

  useEffect(() => { try { document.documentElement.lang = lang; } catch { /* noop */ } }, [lang]);

  const t = useCallback((key) => (T[lang] && T[lang][key]) || T.en[key] || key, [lang]);

  // Format a USD base price into the current currency.
  const fmtUSD = useCallback((usdValue) => {
    const c = CURRENCIES[currency] || CURRENCIES.USD;
    const v = Math.round(usdValue * c.rate);
    return `${c.symbol}${v.toLocaleString()}`;
  }, [currency]);

  const value = useMemo(() => ({
    lang, setLang, currency, setCurrency, t, fmtUSD,
    currencyInfo: CURRENCIES[currency] || CURRENCIES.USD,
  }), [lang, setLang, currency, setCurrency, t, fmtUSD]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LangProvider');
  return ctx;
}
